/**
 * Cart-Drawer — Cross-Selling-Rail (siehe mania-cart-drawer-referenz.html).
 *
 * 1. Prev/Next-Pfeile: identisches scrollBy-Verhalten wie in der Referenz
 *    (Schrittweite = Kartenbreite + Gap, smooth scroll).
 * 2. "+"-Button (.xcard__add): echter Add-to-Cart, da die Referenz selbst
 *    nur eine statische Demo ohne Funktion war. Nutzt denselben
 *    cart_add_url + sections-Payload + morphSection-Ablauf wie
 *    cart-items.js, damit Warenkorb-Inhalt, Summe und dieses Rail nach dem
 *    Hinzufügen konsistent aktualisiert werden.
 */
import { CartAddEvent, CartGroupedSections } from "@theme/events";
import { morphSection } from "@theme/section-renderer";
import { fetchConfig } from "@theme/utilities";

const RAIL_GAP = 12; // px, entspricht dem Gap aus der Referenz

function gatherGroupedSectionIds() {
  /** @type {string[]} */
  const sections = [];
  document.dispatchEvent(new CartGroupedSections(sections));
  return [...new Set(sections)];
}

document.addEventListener("click", (event) => {
  const arrow = event.target instanceof Element ? event.target.closest("[data-rail]") : null;
  if (arrow) {
    const rail = arrow.closest(".cross")?.querySelector("[data-cross-rail]");
    if (!(rail instanceof HTMLElement)) return;

    const card = rail.querySelector(".xcard");
    const step = card instanceof HTMLElement ? card.offsetWidth + RAIL_GAP : 220;
    rail.scrollBy({ left: arrow.getAttribute("data-rail") === "next" ? step : -step, behavior: "smooth" });
    return;
  }

  const addButton = event.target instanceof Element ? event.target.closest("[data-cross-add]") : null;
  if (addButton instanceof HTMLButtonElement) {
    addToCart(addButton);
  }
});

/**
 * @param {HTMLButtonElement} button
 */
async function addToCart(button) {
  if (button.disabled) return;

  const variantId = button.dataset.variantId;
  if (!variantId) return;

  button.disabled = true;
  button.classList.add("is-loading");

  try {
    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: gatherGroupedSectionIds().join(","),
      sections_url: window.location.pathname,
    });

    const response = await fetch(FoxTheme.routes.cart_add_url, fetchConfig("json", { body }));
    const parsed = await response.json();

    if (parsed.status) {
      console.error(parsed.message || "Cart add failed");
      return;
    }

    if (parsed.sections) {
      await Promise.all(
        Object.keys(parsed.sections).map((sectionId) => morphSection(sectionId, parsed.sections[sectionId]))
      );
    }

    document.dispatchEvent(
      new CartAddEvent(parsed, button.id || "cart-cross-sell", {
        itemCount: parsed.quantity || 1,
        variantId: String(variantId),
        source: "cart-cross-sell",
        // Ohne `sections` bleibt <free-shipping-goal> (data-skip-subtree-update)
        // stehen, da es sich nur über diesen Event-Payload selbst aktualisiert,
        // nicht über morphSection.
        sections: parsed.sections,
      })
    );

    button.classList.add("is-added");
    window.setTimeout(() => button.classList.remove("is-added"), 1200);
  } catch (error) {
    console.error("Cross-Sell Add-to-Cart fehlgeschlagen:", error);
  } finally {
    button.disabled = false;
    button.classList.remove("is-loading");
  }
}
